import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { components } from '@/api/generated/schema';

export type AddressSuggestion = components['schemas']['AddressSuggestionResponse'];

/** Mirrors `AddressEndpoints.MinimumQueryLength` — the API refuses anything shorter with a 400. */
export const MIN_SUGGEST_LENGTH = 3;
/** How long after the last keystroke before the API is asked. Autocomplete is billed per request. */
export const SUGGEST_DEBOUNCE_MS = 300;

export interface AddressSuggestions {
  suggestions: AddressSuggestion[];
  /**
   * The API answered 503: the provider could not be asked. Distinct from an
   * empty list on purpose — "nothing matched" and "could not look" must not
   * read the same, and the form's answer to this one is to say so quietly and
   * let the office type the address as they always have.
   */
  unavailable: boolean;
  isFetching: boolean;
}

/**
 * Address suggestions for the property form (M1-23), through the API's proxy —
 * the SPA never holds an Azure Maps credential, so it never talks to Azure Maps.
 *
 * Debounced here rather than in the component, so every caller pays for the
 * same restraint: nothing is asked until typing pauses, and nothing under
 * {@link MIN_SUGGEST_LENGTH} characters is asked at all (the API would 400 it,
 * and it would return half a continent if it did not).
 *
 * `retry: false`: a 503 while someone is typing must not become three more
 * requests per keystroke. The next keystroke is the retry.
 */
export function useAddressSuggestions(term: string, enabled: boolean): AddressSuggestions {
  const trimmed = term.trim();
  const [debounced, setDebounced] = useState(trimmed);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(trimmed), SUGGEST_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [trimmed]);

  // Only a SETTLED term is asked for. Without the equality check, opening the
  // list mid-typing would fire a request for whatever `debounced` still held —
  // in the edit dialog, the property's CURRENT address, a wasted transaction
  // that briefly shows the wrong suggestions under the new text.
  const settled = debounced === trimmed;
  const active = enabled && settled && debounced.length >= MIN_SUGGEST_LENGTH;

  const query = useQuery({
    queryKey: ['addresses', 'suggest', debounced],
    enabled: active,
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<{ suggestions: AddressSuggestion[]; unavailable: boolean }> => {
      const { data, response } = await apiClient.GET('/api/v1/addresses/suggest', {
        params: { query: { q: debounced } },
      });
      if (response.status === 503) {
        return { suggestions: [], unavailable: true };
      }
      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/addresses/suggest failed: ${response.status}`);
      }
      return { suggestions: data, unavailable: false };
    },
  });

  return {
    suggestions: active ? (query.data?.suggestions ?? []) : [],
    unavailable: active && (query.data?.unavailable ?? false),
    isFetching: active && query.isFetching,
  };
}
